import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  listFormat,
  getRoomBedTypeAndRoomSize,
  convertTagToTranslationID,
  numberFormat,
} from '@airasia-common/libraries/i18n/LocaleHelper';
import {
  createRoomLevelAmenitiesMobile,
  createDisplayAmenitiesWeb,
  getMaxRateAmenities,
} from '@airasia-common/libraries/utilities/AmenityHelper';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import { roundPrice } from '@airasia-common/libraries/utilities/formatters';
import PhoenixTheme from '@airasia/phoenix-components/components/Theme';
import BigPointToBeEarned from '@airasia/phoenix-components/components/BigPointToBeEarned';
import {
  CANCELLATION_REFUND_TYPE,
  Colors,
} from '@airasia-common/libraries/constants';
import { AmenitiesList, AmenitiesType } from '../Amenity';
import Wrapper from '../Wrapper';
import Text from '../Text/Text';
import Button from '../Button';
import CollapsibleContainer from '../CollapsibleContainer';
import Row from '../Container/Row';
import Divider from '../Divider';
import { SubBodyText, CaptionText } from '../Text/Heading';
import PriceText from '../Text/PriceText';
import Card from '../Card/Card';
import { ListContentCard } from '../Card/TemplateCard';
import RadioGroup from '../Radio/RadioGroup';
import { useWindowSize } from '../../utils';
import Styled from '../../utils/Styled';
import PopoverTag from '../PopoverTag';
import HotelRoomPrice from '../HotelRoomPrice';
import {
  roomCardLeftContentPropTypes,
  roomCardPropTypes,
  roomCardRightContentPropTypes,
  roomCardTopContentPropTypes,
} from './RoomCard.propTypes';

const BedGroupSelectionNote = Styled(SubBodyText, {
  color: 'greyDark',
  fontStyle: 'italic',
  fontWeight: '500',
  lineHeight: '18px',
});

const PatAtHotel = Styled(CaptionText, {
  fontWeight: '500',
  color: 'tertiaryGreen',
  lineHeight: '18px',
  padding: ['4px', '4px 24px'],
});

const RecommendedLabel = Styled(Card.SubtitleText, {
  fontSize: '12px',
  fontWeight: '500',
  color: 'secondaryDarker',
  padding: '1px 4px',
  borderRadius: '4px',
  backgroundColor: 'secondaryLighter',
  lineHeight: '18px',
  textAlign: 'center',
  cursor: 'pointer',
});

const DesktopWebHeader = ({ name, subtitle }) => (
  <Wrapper display={['none', 'flex']} flexDirection="column">
    <Card.TitleText className="room-name">{name}</Card.TitleText>
    <Card.SubtitleText className="room-description" color="greyDark">
      {subtitle}
    </Card.SubtitleText>
  </Wrapper>
);
DesktopWebHeader.propTypes = {
  name: PropTypes.string,
  subtitle: PropTypes.string,
};
const MobileWebHeader = ({ name, displayAmenities, subtitle }) => (
  <Wrapper display={['flex', 'none']}>
    {displayAmenities.length > 0 ? (
      <CollapsibleContainer
        headerTitle={name}
        className="room-name-mobile"
        headerSubTitle={subtitle}
      >
        <AmenitiesList
          amenities={displayAmenities}
          type={AmenitiesType.ONE_COLUMN_FULL_WIDTH}
          size={16}
        />
      </CollapsibleContainer>
    ) : (
      <Wrapper display="flex" flexDirection="column">
        <Card.TitleText className="room-name-mobile">{name}</Card.TitleText>
      </Wrapper>
    )}
  </Wrapper>
);
MobileWebHeader.propTypes = {
  name: PropTypes.string,
  displayAmenities: PropTypes.array,
  subtitle: PropTypes.string,
};

const RoomCardHeader = ({
  name,
  occupancy,
  otherAmenities,
  roomAmenities,
  subTitle,
  isRecommendRoom,
  isShowRecommended,
  hasSingleOption,
  rates,
}) => {
  const { isMobileView } = useWindowSize();
  const { displayAmenities } = createRoomLevelAmenitiesMobile({
    occupancy,
    otherAmenities,
    roomAmenities,
  });
  const {
    flyerDiscountLabel,
    flyerDiscountDescriptionLabel,
    paymentDiscountType,
    paymentDiscountDescription,
    isFreeCancellation,
    isNonRefundable,
    penaltyTypeLabel,
    penaltyDescriptionLabel,
    refundType,
    instantConfirmationLabel,
    instantConfirmationDescriptionLabel,
    isPayAtHotel,
    isPayLater,
  } = rates[0];
  const isShowRecommendedLabel = isRecommendRoom && isShowRecommended;
  const isShowFlyerDiscountLabel =
    flyerDiscountLabel && flyerDiscountDescriptionLabel;
  const isShowLOBDiscountLabel =
    paymentDiscountType && paymentDiscountDescription;
  const isShowCancellationLabel =
    penaltyTypeLabel && (isFreeCancellation || isNonRefundable);
  const isShowInstantConfirmationLabel =
    instantConfirmationLabel && instantConfirmationDescriptionLabel;
  const isShowTagsLabel =
    isShowRecommendedLabel ||
    isShowFlyerDiscountLabel ||
    isShowLOBDiscountLabel ||
    isShowCancellationLabel ||
    isShowInstantConfirmationLabel ||
    isPayAtHotel ||
    isPayLater;
  return (
    <Wrapper>
      {hasSingleOption && isShowTagsLabel && (
        <Row marginBottom={'8px'}>
          {isShowRecommendedLabel && (
            <Wrapper>
              <RecommendedLabel>
                {translate('tags.recommended')}
              </RecommendedLabel>
            </Wrapper>
          )}
          {isPayAtHotel && (
            <Wrapper>
              <RecommendedLabel
                marginLeft={isShowRecommendedLabel ? '4px' : 0}
                color={Colors.tertiaryGreen}
                backgroundColor={Colors.tertiaryGreenLighter}
              >
                {translate('tags.pay_at_hotel')}
              </RecommendedLabel>
            </Wrapper>
          )}
          {isPayLater && (
            <Wrapper>
              <RecommendedLabel
                marginLeft={isShowRecommendedLabel ? '4px' : 0}
                color={Colors.tertiaryGreen}
                backgroundColor={Colors.tertiaryGreenLighter}
              >
                {translate('tags.pay_later')}
              </RecommendedLabel>
            </Wrapper>
          )}
          {isShowLOBDiscountLabel &&
            (!isMobileView ? (
              <PopoverTag
                content={paymentDiscountDescription}
                position="top"
                background={Colors.darkBlack}
                borderRadius={4}
                left={'50%'}
                marginLeft={'-112px'}
                top={'inherit'}
                bottom={'155%'}
                colorContent={Colors.greyLighter}
                fontSizeContent={'12px'}
                label={
                  <RecommendedLabel
                    marginLeft={
                      isShowRecommendedLabel || isPayAtHotel || isPayLater
                        ? '4px'
                        : 0
                    }
                    color={Colors.tertiaryGreen}
                    backgroundColor={Colors.tertiaryGreenLighter}
                  >
                    {paymentDiscountType}
                  </RecommendedLabel>
                }
              />
            ) : (
              <Wrapper>
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel || isPayAtHotel || isPayLater
                      ? '4px'
                      : 0
                  }
                  color={Colors.tertiaryGreen}
                  backgroundColor={Colors.tertiaryGreenLighter}
                >
                  {paymentDiscountType}
                </RecommendedLabel>
              </Wrapper>
            ))}
          {isShowFlyerDiscountLabel &&
            (!isMobileView ? (
              <PopoverTag
                content={flyerDiscountDescriptionLabel}
                position="top"
                background={Colors.darkBlack}
                borderRadius={4}
                left={'50%'}
                marginLeft={'-112px'}
                top={'inherit'}
                bottom={'155%'}
                fontSizeContent={'12px'}
                colorContent={Colors.greyLighter}
                label={
                  <RecommendedLabel
                    marginLeft={
                      isShowRecommendedLabel ||
                      isPayAtHotel ||
                      isPayLater ||
                      isShowLOBDiscountLabel
                        ? '4px'
                        : 0
                    }
                    color={Colors.tertiaryGreen}
                    backgroundColor={Colors.tertiaryGreenLighter}
                  >
                    {flyerDiscountLabel}
                  </RecommendedLabel>
                }
              />
            ) : (
              <Wrapper>
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel ||
                    isPayAtHotel ||
                    isPayLater ||
                    isShowLOBDiscountLabel
                      ? '4px'
                      : 0
                  }
                  color={Colors.tertiaryGreen}
                  backgroundColor={Colors.tertiaryGreenLighter}
                >
                  {flyerDiscountLabel}
                </RecommendedLabel>
              </Wrapper>
            ))}
          {isShowInstantConfirmationLabel &&
            (!isMobileView ? (
              <PopoverTag
                content={instantConfirmationDescriptionLabel}
                position="top"
                background={Colors.primaryWhite}
                borderRadius={4}
                left={'50%'}
                marginLeft={'-122px'}
                top={'inherit'}
                bottom={'155%'}
                fontSizeContent={'14px'}
                paddingContent={'8px 4px'}
                colorContent={Colors.greyDarker}
                width={220}
                label={
                  <RecommendedLabel
                    marginLeft={
                      isShowRecommendedLabel ||
                      isPayAtHotel ||
                      isPayLater ||
                      isShowLOBDiscountLabel ||
                      isShowFlyerDiscountLabel
                        ? '4px'
                        : 0
                    }
                    color={Colors.secondaryDarker}
                    backgroundColor={Colors.secondaryLighter}
                  >
                    {instantConfirmationLabel}
                  </RecommendedLabel>
                }
              />
            ) : (
              <Wrapper>
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel ||
                    isPayAtHotel ||
                    isPayLater ||
                    isShowLOBDiscountLabel ||
                    isShowFlyerDiscountLabel
                      ? '4px'
                      : 0
                  }
                  color={Colors.secondaryDarker}
                  backgroundColor={Colors.secondaryLighter}
                >
                  {instantConfirmationLabel}
                </RecommendedLabel>
              </Wrapper>
            ))}
          {isShowCancellationLabel && !isMobileView && (
            <PopoverTag
              content={penaltyDescriptionLabel}
              position="top"
              background={Colors.primaryWhite}
              borderRadius={4}
              left={'50%'}
              marginLeft={
                refundType === CANCELLATION_REFUND_TYPE.NO_REFUND
                  ? '-104.5px'
                  : '-154.5px'
              }
              top={'inherit'}
              bottom={'155%'}
              colorContent={Colors.greyDarker}
              width={
                refundType === CANCELLATION_REFUND_TYPE.NO_REFUND ? 185 : 285
              }
              paddingContent={'8px 4px'}
              fontSizeContent={'14px'}
              label={
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel ||
                    isPayAtHotel ||
                    isPayLater ||
                    isShowLOBDiscountLabel ||
                    isShowFlyerDiscountLabel ||
                    isShowInstantConfirmationLabel
                      ? '4px'
                      : 0
                  }
                  color={
                    refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                      ? Colors.tertiaryGreen
                      : Colors.primaryDarker
                  }
                  backgroundColor={
                    refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                      ? Colors.tertiaryGreenLighter
                      : Colors.primaryLight
                  }
                >
                  {penaltyTypeLabel}
                </RecommendedLabel>
              }
            />
          )}
        </Row>
      )}
      <DesktopWebHeader name={name} />
      <MobileWebHeader
        name={name}
        displayAmenities={displayAmenities}
        subtitle={subTitle}
      />
      <Divider display={['block', 'none']} marginY={'12px'} />
    </Wrapper>
  );
};

RoomCardHeader.propTypes = roomCardPropTypes;

const RoomCardLeftContent = ({
  amenities,
  occupancy,
  otherAmenities,
  roomAmenities,
  isShowBedGroupNote,
  isFreeCancellation,
  isNonRefundable,
  penaltyTypeLabel,
}) => {
  const { displayAmenities } = createDisplayAmenitiesWeb({
    rateAmenities: amenities,
    occupancy,
    otherAmenities,
    roomAmenities,
  });

  return (
    <Wrapper>
      <Wrapper>
        {isFreeCancellation && !penaltyTypeLabel && (
          <Row marginBottom={2}>
            <Card.SubtitleText
              className="free-cancellation"
              color={'secondary'}
            >
              {translate('roomCard.optional.freeCancellation')}
            </Card.SubtitleText>
          </Row>
        )}
        {isNonRefundable && !penaltyTypeLabel && (
          <Row marginBottom={2}>
            <Card.SubtitleText className="non-refundable" color={'primary'}>
              {translate('roomCard.optional.nonRefundable')}
            </Card.SubtitleText>
          </Row>
        )}
      </Wrapper>
      {displayAmenities && (
        <Wrapper display={['none', 'flex']} flexDirection="row">
          <AmenitiesList
            type={AmenitiesType.ONE_COLUMN_FULL_WIDTH}
            amenities={
              displayAmenities.length > 4
                ? displayAmenities.slice(0, 4)
                : displayAmenities
            }
            size={20}
          />
          {displayAmenities.length > 4 && (
            <Wrapper marginLeft={5} className="web-amenities">
              <AmenitiesList
                type={AmenitiesType.ONE_COLUMN_FULL_WIDTH}
                amenities={displayAmenities.slice(4)}
                size={20}
              />
            </Wrapper>
          )}
        </Wrapper>
      )}
      {amenities && (
        <Wrapper display={['block', 'none']} className="rate-amenities">
          <AmenitiesList
            type={AmenitiesType.ONE_COLUMN_FULL_WIDTH}
            amenities={getMaxRateAmenities(amenities)}
            size={16}
          />
        </Wrapper>
      )}
      {isShowBedGroupNote && (
        <Wrapper mt={4} className="room-note">
          <BedGroupSelectionNote>
            {translate('roomCard.bedGroupSelectionNote')}
          </BedGroupSelectionNote>
        </Wrapper>
      )}
    </Wrapper>
  );
};

RoomCardLeftContent.propTypes = roomCardRightContentPropTypes;

const RoomCardRightContent = ({
  id,
  rateOptionId,
  baseRate = {},
  perPersonBaseRate = {},
  priceHintText,
  guestHintText,
  guestCount,
  strikethroughPrice,
  totalInclusivePrice,
  roomId,
  onRoomSelect,
  roomName,
  selectedBedGroup,
  cancelPenalties,
  discount,
  bigPoint,
  isPayAtHotel,
  isEnableNewBrandLogo,
  enableTotalGuestPrice,
  discountType,
  perPersonStrikethroughPrice = {},
  isPayLater,
  isSnap,
  tax,
  savings,
  savingsAmount,
  payLaterDate,
}) => {
  const { value: baseValue, currency: baseCurrency } = baseRate;
  const {
    value: perPersonBaseValue,
    currency: perPersonBaseCurrency,
  } = perPersonBaseRate;
  const roundBaseValue = roundPrice(baseValue);
  const roundStrikethroughPrice =
    strikethroughPrice && roundPrice(strikethroughPrice.value);
  const roundPerPersonBaseValue = roundPrice(perPersonBaseValue);
  const roundPerPersonStrikethroughPrice =
    perPersonStrikethroughPrice &&
    roundPrice(perPersonStrikethroughPrice.value);

  const props = {
    baseRate,
    strikethroughPrice,
    guestCount,
    guestHintText,
    priceHintText,
    enableTotalGuestPrice,
    perPersonBaseRate,
    perPersonStrikethroughPrice,
    isSnap,
    roundBaseValue,
    roundStrikethroughPrice,
    roundPerPersonBaseValue,
    roundPerPersonStrikethroughPrice,
  };

  const handleSelect = () => {
    if (onRoomSelect) {
      onRoomSelect({
        roomId,
        rateId: id,
        rateOptionId,
        roomName,
        price: totalInclusivePrice,
        bedGroupId: selectedBedGroup.id,
        cancelPenalties,
        tax,
        savings,
        savingsAmount,
        strikethroughPrice,
      });
    }
  };

  const bigPointsIconBgFill = isEnableNewBrandLogo
    ? {
        bigPointsIconBgFill: '#F99D1C',
      }
    : {};

  return (
    <>
      {discount &&
        (discountType === 2 ? (
          <Card.PriceOffTextV2 className="price-off">
            {discount}
          </Card.PriceOffTextV2>
        ) : (
          <Card.PriceOffText className="price-off">
            {discount}
          </Card.PriceOffText>
        ))}

      <HotelRoomPrice props={props} />

      <Wrapper
        className="select-room-button"
        marginTop={'12px'}
        width={['min(140px, 100%)', '164px']}
      >
        <Button big secondary block padding="12px 0" onClick={handleSelect}>
          {isPayAtHotel || isPayLater
            ? translate('roomCard.reserveNow')
            : translate('roomCard.bookNow')}
        </Button>
      </Wrapper>
      {(isPayAtHotel || isPayLater) && (
        <Wrapper
          marginTop={'8px'}
          marginBottom={['11px', 0]}
          width={['min(140px, 100%)', '164px']}
          borderRadius={'4px'}
          backgroundColor={'tertiaryGreenLighter'}
          textAlign={'center'}
        >
          <PatAtHotel>
            {isPayAtHotel ? translate('tags.pay_at_hotel') : payLaterDate}
          </PatAtHotel>
        </Wrapper>
      )}
      {!isPayAtHotel && bigPoint && (
        <Wrapper mt="8px">
          <PhoenixTheme>
            <BigPointToBeEarned
              template={bigPoint.template}
              pointTemplate={bigPoint.pointTemplate}
              pointValue={bigPoint.pointValue}
              multiplierTemplate={bigPoint.multiplierTemplate}
              multiplierValue={bigPoint.multiplierValue}
              customProperties={bigPointsIconBgFill}
            />
          </PhoenixTheme>
        </Wrapper>
      )}
    </>
  );
};

RoomCardRightContent.propTypes = roomCardLeftContentPropTypes;

const RoomCardTopContent = ({
  index,
  hasSingleOption,
  bedGroups,
  selectedBedGroups,
  setSelectedBedGroups,
  isRecommended,
  isShowRecommended,
  flyerDiscountLabel,
  flyerDiscountDescriptionLabel,
  paymentDiscountType,
  paymentDiscountDescription,
  isFreeCancellation,
  isNonRefundable,
  penaltyTypeLabel,
  penaltyDescriptionLabel,
  refundType,
  instantConfirmationLabel,
  instantConfirmationDescriptionLabel,
  isPayAtHotel,
  isPayLater,
}) => {
  const { isMobileView } = useWindowSize();

  const displayedBedGroups = bedGroups.map(bedGroup => ({
    id: bedGroup.id,
    text: getRoomBedTypeAndRoomSize({
      bedSize: bedGroup.description,
      squareMeterArea: bedGroup.squareMeterArea,
    }),
  }));

  const onChangeBedGroup = (selectedItem, currentIndex) => {
    const newSelectedBedGroups = [...selectedBedGroups];
    newSelectedBedGroups[currentIndex] = selectedItem;
    setSelectedBedGroups(newSelectedBedGroups);
  };
  const hasSingleBed = bedGroups.length < 2;
  const isShowRecommendedLabel = isRecommended && isShowRecommended;
  const isShowFlyerDiscountLabel =
    flyerDiscountLabel && flyerDiscountDescriptionLabel;
  const isShowLOBDiscountLabel =
    paymentDiscountType && paymentDiscountDescription;
  const isShowCancellationLabel =
    penaltyTypeLabel && (isFreeCancellation || isNonRefundable);
  const isShowInstantConfirmationLabel =
    instantConfirmationLabel && instantConfirmationDescriptionLabel;

  if (hasSingleOption && hasSingleBed && isMobileView) {
    return (
      <>
        {isShowCancellationLabel && (
          <Wrapper>
            <Row>
              <Card.SubtitleText
                color={
                  refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                    ? 'tertiaryGreen'
                    : 'primaryDarker'
                }
                fontSize="12px"
                lineHeight="15px"
              >
                {refundType === CANCELLATION_REFUND_TYPE.NO_REFUND
                  ? penaltyDescriptionLabel
                  : penaltyTypeLabel}
              </Card.SubtitleText>
            </Row>
            {refundType !== CANCELLATION_REFUND_TYPE.NO_REFUND && (
              <Row>
                <SubBodyText color="greyDark" fontSize="12px" lineHeight="15px">
                  {penaltyDescriptionLabel}
                </SubBodyText>
              </Row>
            )}
          </Wrapper>
        )}
      </>
    );
  }
  return (
    <Wrapper>
      {!hasSingleOption && (
        <Row mt={3}>
          <Text
            className="option-text"
            fontSize={['14px', '16px']}
            lineHeight={['18px', '21px']}
          >
            {`${translate('roomCard.option')} ${index + 1}`}
          </Text>
          {isShowRecommendedLabel && (
            <Wrapper>
              <RecommendedLabel marginLeft={'8px'}>
                {translate('tags.recommended')}
              </RecommendedLabel>
            </Wrapper>
          )}
          {isPayAtHotel && (
            <Wrapper>
              <RecommendedLabel
                marginLeft={isShowRecommendedLabel ? '4px' : '8px'}
                color={Colors.tertiaryGreen}
                backgroundColor={Colors.tertiaryGreenLighter}
              >
                {translate('tags.pay_at_hotel')}
              </RecommendedLabel>
            </Wrapper>
          )}
          {isPayLater && (
            <Wrapper>
              <RecommendedLabel
                marginLeft={isShowRecommendedLabel ? '4px' : '8px'}
                color={Colors.tertiaryGreen}
                backgroundColor={Colors.tertiaryGreenLighter}
              >
                {translate('tags.pay_later')}
              </RecommendedLabel>
            </Wrapper>
          )}
          {isShowLOBDiscountLabel &&
            (!isMobileView ? (
              <PopoverTag
                content={paymentDiscountDescription}
                position="top"
                background={Colors.darkBlack}
                borderRadius={4}
                left={'50%'}
                marginLeft={'-112px'}
                top={'inherit'}
                bottom={'155%'}
                colorContent={Colors.greyLighter}
                fontSizeContent={'12px'}
                label={
                  <RecommendedLabel
                    marginLeft={
                      isShowRecommendedLabel || isPayAtHotel || isPayLater
                        ? '4px'
                        : '8px'
                    }
                    color={Colors.tertiaryGreen}
                    backgroundColor={Colors.tertiaryGreenLighter}
                  >
                    {paymentDiscountType}
                  </RecommendedLabel>
                }
              />
            ) : (
              <Wrapper>
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel || isPayAtHotel || isPayLater
                      ? '4px'
                      : '8px'
                  }
                  color={Colors.tertiaryGreen}
                  backgroundColor={Colors.tertiaryGreenLighter}
                >
                  {paymentDiscountType}
                </RecommendedLabel>
              </Wrapper>
            ))}
          {isShowFlyerDiscountLabel &&
            (!isMobileView ? (
              <PopoverTag
                content={flyerDiscountDescriptionLabel}
                position="top"
                background={Colors.darkBlack}
                borderRadius={4}
                left={'50%'}
                marginLeft={'-112px'}
                top={'inherit'}
                bottom={'155%'}
                colorContent={Colors.greyLighter}
                fontSizeContent={'12px'}
                label={
                  <RecommendedLabel
                    marginLeft={
                      isShowRecommendedLabel ||
                      isPayAtHotel ||
                      isPayLater ||
                      isShowLOBDiscountLabel
                        ? '4px'
                        : '8px'
                    }
                    color={Colors.tertiaryGreen}
                    backgroundColor={Colors.tertiaryGreenLighter}
                  >
                    {flyerDiscountLabel}
                  </RecommendedLabel>
                }
              />
            ) : (
              <Wrapper>
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel ||
                    isPayAtHotel ||
                    isPayLater ||
                    isShowLOBDiscountLabel
                      ? '4px'
                      : '8px'
                  }
                  color={Colors.tertiaryGreen}
                  backgroundColor={Colors.tertiaryGreenLighter}
                >
                  {flyerDiscountLabel}
                </RecommendedLabel>
              </Wrapper>
            ))}
          {isShowInstantConfirmationLabel &&
            (!isMobileView ? (
              <PopoverTag
                content={instantConfirmationDescriptionLabel}
                position="top"
                background={Colors.primaryWhite}
                borderRadius={4}
                left={'50%'}
                marginLeft={'-122px'}
                top={'inherit'}
                bottom={'155%'}
                colorContent={Colors.greyDarker}
                fontSizeContent={'14px'}
                paddingContent={'8px 4px'}
                width={220}
                label={
                  <RecommendedLabel
                    marginLeft={
                      isShowRecommendedLabel ||
                      isPayAtHotel ||
                      isPayLater ||
                      isShowLOBDiscountLabel ||
                      isShowFlyerDiscountLabel
                        ? '4px'
                        : '8px'
                    }
                    color={Colors.secondaryDarker}
                    backgroundColor={Colors.secondaryLighter}
                  >
                    {instantConfirmationLabel}
                  </RecommendedLabel>
                }
              />
            ) : (
              <Wrapper>
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel ||
                    isPayAtHotel ||
                    isPayLater ||
                    isShowLOBDiscountLabel ||
                    isShowFlyerDiscountLabel
                      ? '4px'
                      : '8px'
                  }
                  color={Colors.secondaryDarker}
                  backgroundColor={Colors.secondaryLighter}
                >
                  {instantConfirmationLabel}
                </RecommendedLabel>
              </Wrapper>
            ))}
          {isShowCancellationLabel && !isMobileView && (
            <PopoverTag
              content={penaltyDescriptionLabel}
              position="top"
              background={Colors.primaryWhite}
              colorContent={Colors.greyDarker}
              width={
                refundType === CANCELLATION_REFUND_TYPE.NO_REFUND ? 185 : 285
              }
              borderRadius={4}
              left={'50%'}
              marginLeft={
                refundType === CANCELLATION_REFUND_TYPE.NO_REFUND
                  ? '-104.5px'
                  : '-154.5px'
              }
              paddingContent={'8px 4px'}
              fontSizeContent={'14px'}
              top={'inherit'}
              bottom={'155%'}
              label={
                <RecommendedLabel
                  marginLeft={
                    isShowRecommendedLabel ||
                    isPayAtHotel ||
                    isPayLater ||
                    isShowLOBDiscountLabel ||
                    isShowFlyerDiscountLabel ||
                    isShowInstantConfirmationLabel
                      ? '4px'
                      : '8px'
                  }
                  color={
                    refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                      ? Colors.tertiaryGreen
                      : Colors.primaryDarker
                  }
                  backgroundColor={
                    refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                      ? Colors.tertiaryGreenLighter
                      : Colors.primaryLight
                  }
                >
                  {penaltyTypeLabel}
                </RecommendedLabel>
              }
            />
          )}
        </Row>
      )}
      {bedGroups.length === 1 ? (
        <Wrapper mb={isShowCancellationLabel && isMobileView ? 2 : 4}>
          <SubBodyText color="greyDark" fontSize="12px">
            {displayedBedGroups[0].text}
          </SubBodyText>
        </Wrapper>
      ) : (
        <Wrapper mb={2}>
          <RadioGroup
            selectedId={selectedBedGroups[index]?.id}
            isVertical={isMobileView}
            options={displayedBedGroups}
            spacing={8}
            onChange={item => onChangeBedGroup(item, index)}
            itemCustomStyles={
              bedGroups &&
              bedGroups.map((item, index) =>
                index === 1 && !isMobileView ? { marginLeft: 42 } : {}
              )
            }
            textColor="greyDark"
          />
        </Wrapper>
      )}
      {isShowCancellationLabel && isMobileView && (
        <Wrapper>
          <Row>
            <Card.SubtitleText
              color={
                refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                  ? 'tertiaryGreen'
                  : 'primaryDarker'
              }
              fontSize="12px"
              lineHeight="15px"
            >
              {refundType === CANCELLATION_REFUND_TYPE.NO_REFUND
                ? penaltyDescriptionLabel
                : penaltyTypeLabel}
            </Card.SubtitleText>
          </Row>
          {refundType !== CANCELLATION_REFUND_TYPE.NO_REFUND && (
            <Row>
              <SubBodyText color="greyDark" fontSize="12px" lineHeight="15px">
                {penaltyDescriptionLabel}
              </SubBodyText>
            </Row>
          )}
        </Wrapper>
      )}
    </Wrapper>
  );
};
RoomCardTopContent.propTypes = roomCardTopContentPropTypes;

const RoomCard = props => {
  const {
    id,
    image,
    onLoadImage,
    leftPillTexts,
    leftPillType,
    rightPillTexts,
    rates,
    priceHintText,
    guestHintText,
    onRoomSelect,
    guestCount,
    name,
    locale,
    bigPoint,
    displayPerGuestPrice,
    enableTotalGuestPrice,
    isShowRecommended,
    isEnableNewBrandLogo,
    discountType,
    filter,
    isLoggedIn,
    bigMembership,
    imageCount,
    onRoomImageClickHandler,
    isSnap,
  } = props;
  const [selectedBedGroupsInRoom, setSelectedBedGroup] = useState(
    rates.map(rate => rate.bedGroups[0])
  );
  const { isMobileView } = useWindowSize();
  const hasSingleOption = rates.length === 1;

  const convertedLeftPillTexts = leftPillTexts?.map(label =>
    convertTagToTranslationID(label)
  );
  const convertedRightPillTexts = rightPillTexts?.map(label =>
    convertTagToTranslationID(label)
  );

  const leftPillText = listFormat(convertedLeftPillTexts, locale);
  const rightPillText = listFormat(convertedRightPillTexts, locale);

  const { occupancy, otherAmenities, roomAmenities } = props;

  const { availableRooms } = rates[0];
  const roomsLeft =
    availableRooms &&
    translate('roomCard.roomsLeftText', { smart_count: availableRooms });
  const isShowSubTitle = rates.length === 1 && rates[0].bedGroups.length === 1;
  const subTitle = isShowSubTitle
    ? getRoomBedTypeAndRoomSize({
        bedSize: rates[0].bedGroups[0].description,
        squareMeterArea: rates[0].bedGroups[0].squareMeterArea,
      })
    : '';
  return (
    <ListContentCard
      onRoomImageClickHandler={onRoomImageClickHandler}
      imageCount={imageCount}
      thumbnailStyle={{ height: [128, 208], width: ['100%', 376] }}
      thumbnailImage={image}
      onLoadThumbnailImage={onLoadImage}
      leftPillText={leftPillText}
      leftPillType={leftPillType}
      rightPillText={roomsLeft ? roomsLeft : rightPillText}
      headerComponent={
        <RoomCardHeader
          {...props}
          subTitle={subTitle}
          hasSingleOption={hasSingleOption}
        />
      }
      listContentComponents={rates.map((rate, index) => ({
        id: rate.id,
        topContentComponent: (
          <RoomCardTopContent
            index={index}
            isShowRecommended={isShowRecommended}
            {...rate}
            selectedBedGroups={selectedBedGroupsInRoom}
            setSelectedBedGroups={setSelectedBedGroup}
            hasSingleOption={hasSingleOption}
          />
        ),
        leftContentComponent: (
          <RoomCardLeftContent
            index={index}
            {...rate}
            occupancy={occupancy}
            otherAmenities={otherAmenities}
            roomAmenities={roomAmenities}
            isShowBedGroupNote={rate.bedGroups.length > 1 && !isMobileView}
          />
        ),
        rightContentComponent: (
          <RoomCardRightContent
            priceHintText={priceHintText}
            guestHintText={guestHintText}
            guestCount={guestCount}
            onRoomSelect={onRoomSelect}
            roomId={id}
            roomName={name}
            bigPoint={bigPoint}
            selectedBedGroup={selectedBedGroupsInRoom[index]}
            {...rate}
            isSnap={isSnap}
            baseRate={
              !isSnap
                ? displayPerGuestPrice
                  ? rate.perPersonBaseRate
                  : bigMembership &&
                    filter.applyBigpoints &&
                    rate.baseRateAfterBigPointsDeduction
                  ? rate.baseRateAfterBigPointsDeduction
                  : rate.baseRate
                : rate.baseRate
            }
            strikethroughPrice={
              displayPerGuestPrice
                ? rate.perPersonStrikethroughPrice
                : rate.strikethroughPrice
            }
            perPersonBaseRate={rate.perPersonBaseRate}
            perPersonStrikethroughPrice={rate.perPersonStrikethroughPrice}
            isEnableNewBrandLogo={isEnableNewBrandLogo}
            enableTotalGuestPrice={enableTotalGuestPrice}
            displayPerGuestPrice={displayPerGuestPrice}
            discountType={discountType}
          />
        ),
        bottomContentComponent: isMobileView && (
          <>
            <Row pb={'16px'}>
              {rate.paymentDiscountDescription && (
                <Wrapper mt={'16px'} pb={rate.bedGroups.length > 1 ? '4px' : 0}>
                  <Text
                    color={'greyDark'}
                    fontSize={'12px'}
                    fontWeight={'400'}
                    lineHeight={'18px'}
                  >
                    {rate.paymentDiscountDescription}
                  </Text>
                </Wrapper>
              )}
              {rate.bedGroups.length > 1 && (
                <Wrapper
                  mt={rate.paymentDiscountDescription ? 0 : '16px'}
                  className="room-note"
                >
                  <BedGroupSelectionNote>
                    {translate('roomCard.bedGroupSelectionNote')}
                  </BedGroupSelectionNote>
                </Wrapper>
              )}
            </Row>
          </>
        ),
      }))}
      separator
    />
  );
};

RoomCard.propTypes = roomCardPropTypes;
RoomCard.defaultProps = {
  displayPerGuestPrice: false,
  enableTotalGuestPrice: false,
};

export default RoomCard;
